import { el, clear } from './dom.js';
import { ENDPOINTS } from '../config.js';

/**
 * AI内見パネル。
 * 物件事実（facts）と「いま見ている部屋」をサーバに渡し、
 * その範囲で答えさせる。推測で家賃や条件を作らせないことが要件。
 */
export function createAiPanel(container, property, { onClose }) {
  const log = el('div', { class: 'ai-log' });
  const input = el('input', {
    class: 'ai-input',
    type: 'text',
    placeholder: '例）このリビングに2人掛けソファは置けますか？',
    maxlength: '300',
    autocomplete: 'off',
  });
  const sendBtn = el('button', { class: 'btn btn-send', type: 'submit' }, '送信');

  /** 直近の会話履歴。毎回新しい配列に差し替える（破壊的変更をしない） */
  let history = [];
  // 写真モードでは viewpoints が空なので、写真側の部屋名を使う
  let currentRoom = property.viewpoints[0]?.name ?? property.photos[0]?.room ?? '';
  let busy = false;

  const form = el('form', { class: 'ai-form' }, [input, sendBtn]);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void ask(input.value);
  });

  const root = el('aside', { class: 'ai-panel', 'aria-label': 'AI内見' }, [
    el('header', { class: 'ai-head' }, [
      el('span', {}, 'AI内見サポート'),
      el('button', { class: 'ai-close', type: 'button', onClick: onClose, 'aria-label': '閉じる' }, '×'),
    ]),
    log,
    form,
  ]);
  container.appendChild(root);

  appendMessage('assistant', `${property.name} の内見サポートです。部屋の広さ、設備、周辺のことなどお気軽にどうぞ。`);

  async function ask(rawText) {
    const text = rawText.trim();
    if (text.length === 0 || busy) return;
    if (text.length > 300) {
      appendMessage('error', '質問は300文字以内で入力してください');
      return;
    }

    setBusy(true);
    input.value = '';
    appendMessage('user', text);
    const thinking = appendMessage('assistant', '…');

    try {
      const answer = await postQuestion({ property, room: currentRoom, history, question: text });
      thinking.textContent = answer;
      history = [...history, { role: 'user', content: text }, { role: 'assistant', content: answer }].slice(-10);
    } catch (error) {
      console.error('[naiken-3dgs][ai]', error);
      thinking.remove();
      appendMessage('error', 'うまく回答できませんでした。少し時間をおいて試すか、担当者にお問い合わせください。');
    } finally {
      setBusy(false);
    }
  }

  function setBusy(next) {
    busy = next;
    input.disabled = next;
    sendBtn.disabled = next;
  }

  function appendMessage(role, text) {
    const node = el('p', { class: `ai-msg ai-${role}` }, text);
    log.appendChild(node);
    log.scrollTop = log.scrollHeight;
    return node;
  }

  return {
    setVisible(visible) {
      root.classList.toggle('is-open', visible);
      if (visible) input.focus();
    },
    setRoom(name) {
      currentRoom = name;
    },
    reset() {
      history = [];
      clear(log);
    },
  };
}

async function postQuestion({ property, room, history, question }) {
  const res = await fetch(ENDPOINTS.ai, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ propertyId: property.id, room, history, question }),
  });
  if (!res.ok) throw new Error(`AI応答エラー ${res.status}`);
  const data = await res.json();
  return normalizeAiAnswer(data?.answer);
}

/** サーバ側の制約が外れても、パネルでは短く読みやすい回答だけを表示する。 */
export function normalizeAiAnswer(answer) {
  if (typeof answer !== 'string' || answer.trim().length === 0) {
    throw new Error('AI応答が空でした');
  }
  return Array.from(answer.trim()).slice(0, 200).join('');
}
